import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Heart, User as UserIcon, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { AuthSheet } from './AuthSheet';
import { NotificationBell } from './NotificationBell';
import { HeaderSearchBar } from './HeaderSearchBar';
import { MobileSearchSheet } from './MobileSearchSheet';
import { cn } from '@/lib/utils';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import motorevealLogo from '@/assets/motoreveal-logo.png';

export const Navbar: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwnerOrAdmin, setIsOwnerOrAdmin] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureEnabled } = useFeatureFlags();
  const [pendingRoute, setPendingRoute] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await supabase.auth.signOut({ scope: 'local' });
      setUser(null);
      setIsAdmin(false);
      setIsOwnerOrAdmin(false);
      setAvatarUrl(null);
      navigate('/', { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (import.meta.env.DEV) console.debug('[auth]', event);
      setUser(session?.user ?? null);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsOwnerOrAdmin(false);
        setAvatarUrl(null);
        return;
      }
      
      // Fetch roles and profile in parallel
      const [rolesRes, profileRes] = await Promise.all([
        supabase.from('user_roles').select('role').eq('user_id', user.id).in('role', ['admin', 'owner']),
        supabase.from('profiles').select('avatar_url').eq('user_id', user.id).maybeSingle()
      ]);
      
      const roles = rolesRes.data?.map(r => r.role) || [];
      setIsAdmin(roles.includes('admin'));
      setIsOwnerOrAdmin(roles.length > 0);
      setAvatarUrl(profileRes.data?.avatar_url || null);
    };
    fetchUserData();
  }, [user]);

  useEffect(() => {
    if (user && pendingRoute) {
      navigate(pendingRoute);
      setPendingRoute(null);
      setIsAuthOpen(false);
    }
  }, [user, pendingRoute, navigate]);

  const isActive = (path: string) => location.pathname === path;

  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
    <Link 
      to={to} 
      className={cn(
        "px-4 py-2 text-sm font-medium rounded-full transition-colors",
        isActive(to) 
          ? "bg-primary text-primary-foreground" 
          : "text-foreground/70 hover:text-foreground hover:bg-secondary/50"
      )}
    >
      {children}
    </Link>
  );

  // Check if we're on the homepage to show the search bar
  const isHomepage = location.pathname === '/' || location.pathname === '/experiences';

  return createPortal(
    <>
      <nav className={cn(
        "fixed top-0 left-0 right-0 z-[2000] transition-all duration-300",
        "bg-[#f9f9f9]"
      )}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img src={motorevealLogo} alt="MotoReveal" className="h-5 md:h-6" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1 bg-secondary rounded-full p-1">
              <NavLink to="/">Events</NavLink>
              <NavLink to="/experiences">Experiences</NavLink>
              <NavLink to="/activity">Community</NavLink>
              <NavLink to="/map">Map</NavLink>
            </div>

            {/* Desktop Auth */}
            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <>
                  <NotificationBell userId={user.id} />
                  <Link to="/dashboard?section=favorites" className="p-2.5 rounded-full hover:bg-secondary transition-colors">
                    <Heart className="w-5 h-5" />
                  </Link>
                  <div className="relative group">
                    <button className="flex items-center gap-2 p-1.5 pl-3 border border-border rounded-full hover:shadow-md transition-shadow">
                      <Menu className="w-4 h-4" />
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="Profile" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <UserIcon className="w-4 h-4 text-primary" />
                        </div>
                      )}
                    </button>
                    <div className="absolute right-0 top-full mt-2 w-56 bg-background rounded-xl shadow-xl border border-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2 z-50">
                      <Link to="/dashboard" className="block px-4 py-2.5 text-sm hover:bg-secondary">Dashboard</Link>
                      <Link to="/dashboard?section=events" className="block px-4 py-2.5 text-sm hover:bg-secondary">My Events</Link>
                      <Link to="/dashboard?section=experiences" className="block px-4 py-2.5 text-sm hover:bg-secondary">My Experiences</Link>
                      {isFeatureEnabled('motosenseiq') && (
                        <Link to="/dashboard?section=driveway" className="block px-4 py-2.5 text-sm hover:bg-secondary">MotoSenseIQ</Link>
                      )}
                      <Link to="/dashboard?section=favorites" className="block px-4 py-2.5 text-sm hover:bg-secondary">Favorites</Link>
                      {isAdmin && (
                        <Link to="/admin" className="block px-4 py-2.5 text-sm hover:bg-secondary">
                          Admin
                        </Link>
                      )}
                      <div className="border-t border-border my-2" />
                      <button 
                        onClick={handleSignOut} 
                        disabled={isSigningOut} 
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary"
                      >
                        {isSigningOut ? 'Signing out...' : 'Sign out'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => setIsAuthOpen(true)} 
                    className="px-4 py-2.5 text-sm font-medium hover:bg-secondary rounded-full"
                  >
                    Sign in
                  </button>
                  <button 
                    onClick={() => setIsAuthOpen(true)} 
                    className="px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-full hover:bg-primary/90"
                  >
                    Sign up
                  </button>
                </>
              )}
            </div>

            {/* Mobile Actions */}
            <div className="md:hidden flex items-center gap-1">
              {user && (
                <>
                  <NotificationBell userId={user.id} />
                  <Link to="/dashboard?section=favorites" className="p-2 rounded-full hover:bg-secondary transition-colors">
                    <Heart className="w-5 h-5" />
                  </Link>
                </>
              )}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                className="p-2 rounded-full hover:bg-secondary"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar - Below Tabs (Airbnb Style) - Desktop */}
        {isHomepage && (
          <div className="hidden md:block py-4 pb-6 bg-[#f9f9f9]">
            <HeaderSearchBar />
          </div>
        )}

        {/* Mobile Search - Bottom Sheet Trigger */}
        {isHomepage && (
          <div className="md:hidden px-4 pb-4 bg-[#f9f9f9]">
            <MobileSearchSheet />
          </div>
        )}

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 top-16 bg-background z-[2500] animate-fade-in">
            <div className="flex flex-col p-6 gap-2 bg-background h-full">
              <Link 
                to="/" 
                onClick={() => setIsMobileMenuOpen(false)} 
                className={cn("px-4 py-3 rounded-xl text-lg font-medium", isActive('/') ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}
              >
                Events
              </Link>
              <Link 
                to="/experiences" 
                onClick={() => setIsMobileMenuOpen(false)} 
                className={cn("px-4 py-3 rounded-xl text-lg font-medium", isActive('/experiences') ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}
              >
                Experiences
              </Link>
              <Link 
                to="/map" 
                onClick={() => setIsMobileMenuOpen(false)} 
                className={cn("px-4 py-3 rounded-xl text-lg font-medium", isActive('/map') ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}
              >
                Map
              </Link>
              <Link 
                to="/activity" 
                onClick={() => setIsMobileMenuOpen(false)} 
                className={cn("px-4 py-3 rounded-xl text-lg font-medium", isActive('/activity') ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}
              >
                Community
              </Link>
              <div className="border-t border-border my-4" />
              {user ? (
              <>
                  <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 rounded-xl text-lg font-medium hover:bg-secondary">Dashboard</Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 rounded-xl text-lg font-medium hover:bg-secondary">
                      Admin
                    </Link>
                  )}
                  <div className="border-t border-border my-4" />
                  <button 
                    onClick={() => { setIsMobileMenuOpen(false); handleSignOut(); }} 
                    disabled={isSigningOut} 
                    className="px-4 py-3 rounded-xl text-lg font-medium text-left hover:bg-secondary"
                  >
                    {isSigningOut ? 'Signing out...' : 'Sign out'}
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => { setIsAuthOpen(true); setIsMobileMenuOpen(false); }} 
                  className="px-4 py-3 rounded-xl text-lg font-medium bg-primary text-primary-foreground text-center"
                >
                  Sign in
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
      <AuthSheet 
        isOpen={isAuthOpen} 
        onClose={() => { setIsAuthOpen(false); setPendingRoute(null); }} 
      />
    </>,
    document.body
  );
};
